import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { NewsArticle } from '@/types/news';
import { formatDate, getCategoryName } from '@/utils/news';

interface HeroCarouselProps {
  articles: NewsArticle[];
}

export default function HeroCarousel({ articles }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const touchStart = useRef<number | null>(null);
  const count = articles.length;

  useEffect(() => {
    if (count <= 1) return;
    const timer = setInterval(() => setIndex((v) => (v + 1) % count), 5000);
    return () => clearInterval(timer);
  }, [count]);

  if (count === 0) {
    return (
      <div className="relative w-full h-[360px] md:h-[480px] rounded-lg overflow-hidden bg-primary-800 flex items-center justify-center text-center px-6">
        <div>
          <h2 className="font-heading font-black text-2xl md:text-4xl text-background-50">让信息更有价值</h2>
          <p className="mt-3 text-sm md:text-base text-primary-100/85">9·1牛网 · 全方位资讯与内容服务平台</p>
        </div>
      </div>
    );
  }

  const go = (next: number) => setIndex(((next % count) + count) % count);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStart.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStart.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(delta) > 50) go(delta < 0 ? index + 1 : index - 1);
    touchStart.current = null;
  };

  return (
    <div
      className="relative w-full h-[360px] md:h-[480px] rounded-lg overflow-hidden bg-background-900"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {articles.map((article, i) => (
        <Link
          key={article.id}
          to={`/detail/${article.id}`}
          className={`absolute inset-0 transition-opacity duration-700 cursor-pointer ${
            i === index ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'
          }`}
        >
          <img
            src={article.cover}
            alt={`${article.title} 焦点图`}
            title={`${article.title} - 9·1牛网`}
            className="w-full h-full object-top object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
          <div className="absolute bottom-0 left-0 right-0 p-5 md:p-10">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-accent-500 text-background-50">
                {getCategoryName(article.category)}
              </span>
              {article.tags.slice(0, 1).map((tag) => (
                <span key={tag} className="px-2.5 py-1 rounded-full text-xs font-medium bg-background-50/20 text-background-50 backdrop-blur">
                  {tag}
                </span>
              ))}
            </div>
            <h2 className="font-heading font-black text-xl md:text-3xl text-background-50 max-w-3xl leading-snug line-clamp-2">
              {article.title}
            </h2>
            <p className="hidden sm:block mt-3 text-sm md:text-base text-background-100/95 max-w-2xl line-clamp-2">
              {article.summary}
            </p>
            <p className="mt-3 text-xs text-background-100/75">{formatDate(article.publishTime)} · 9·1牛网</p>
          </div>
        </Link>
      ))}

      <button
        type="button"
        aria-label="上一张"
        onClick={() => go(index - 1)}
        className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-black/35 text-background-50 hover:bg-black/55 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-xl" />
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => go(index + 1)}
        className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-black/35 text-background-50 hover:bg-black/55 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-xl" />
      </button>

      <div className="absolute bottom-4 right-5 z-20 flex items-center gap-2">
        {articles.map((a, i) => (
          <button
            key={a.id}
            type="button"
            aria-label={`切换到第${i + 1}张`}
            onClick={() => go(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? 'w-6 bg-accent-500' : 'w-1.5 bg-background-50/60 hover:bg-background-50'
            }`}
          />
        ))}
      </div>
    </div>
  );
}